// Terms of Use HTML content
export const termsOfUseHtml = `
    <section>
        <p>
            These website TERMS OF USE, as amended (the <u>“Terms”</u>) govern your access to and use of the websites, portals,
            customer account, online services, and related systems of <b>W8 Shipping LLC</b>, a Georgia limited liability company
            (referred to as the <u>“Company”</u> or <u>“W8“</u> or <u>“we,”</u> <u>“our,”</u> and <u>“us”</u>), including all content, functionality, and
            services offered on or through them (collectively, the <u>“Site”</u>). By accessing or using the Site, you acknowledge
            that you have read, understood, and agree to be bound by the following Terms.
        </p>
    </section>

    <!-- 1. Terms -->
    <section>
        <h3>1. Terms</h3>
        <ul class="decimal-list" style="--prefix: '1'">
            <li>
                <p>
                    <u>Terms of Use.</u> These web Terms will control your use and access to the Site and remain subject to the
                    Company’s other applicable Terms & Conditions including the Company’s Privacy Policy (available at
                    <a target="_blank" href="https://www.w8shipping.com">www.w8shipping.com</a>) and all service terms and conditions applicable to the services offered by the Company
                    including <u>shipping</u>, <u>logistics</u>, <u>freight</u>, and other services offered by the Company or its Related Parties
                    through the Site (collectively, the <u>“Service Terms”</u>).
                </p>
            </li>
            <li>
                <p>
                    <u>Conflicts.</u> In the event of a conflict between these web Terms and any other Service Terms of the
                    Company, the Service Terms will control.
                </p>
            </li>
            <li>
                <p>
                    <u>Consent to Terms.</u> By accessing the Site, you agree to these Terms with the same force and legal effect
                    as a signed written agreement, and you further represent that you are at least the age of majority in your
                    jurisdiction and have the authority to bind yourself and any person or entity on whose behalf you use the
                    Site.
                </p>
            </li>
            <li>
                <p>
                    <u>Non-Consent to Terms.</u> If you do not agree to these Terms, you are not permitted to access the Site.
                </p>
            </li>
            <li>
                <p>
                    <u>Scope of Terms; Related Parties.</u> These Terms govern your use of the Site and your interactions with
                    W8 Shipping LLC. The Site and certain services offered through the Site may incorporate, rely on, or involve
                    the participation of the Company’s affiliates, subsidiaries, agents, subcontractors, service providers,
                    direct or indirect interest holders, and their respective owners, officers, directors, employees,
                    representatives, successors, and assigns (collectively, <u>“Related Parties”</u>). To the extent any Related Party
                    is involved in providing or supporting the Site or Company services, these Terms inure to the benefit of,
                    and may be enforced by, such Related Parties; provided, however, that nothing in this Section shall make the
                    Company responsible or liable for any Related Parties or for the acts, omissions, or representations of
                    third parties, including any third‑party carriers, vendors, Related Parties, or other service providers
                    whose services are displayed, referenced, or accessible through the Site or integrated into the Company’s
                    services.
                </p>
            </li>
        </ul>
    </section>

    <!-- 2. Using the Site -->
    <section>
        <h3>2. Using the Site</h3>
        <ul class="decimal-list" style="--prefix: '2'">
            <li>
                <p>
                    <u>Access License.</u> The Company grants you a limited, revocable, non-exclusive, non-transferable license
                    to access and use the Site solely for legitimate business purposes related to evaluating or obtaining our
                    services.
                </p>
            </li>
            <li>
                <p>
                    <u>Acceptable Use.</u> By accessing the Site, you agree to use the Site in a manner that complies with these
                    Terms and all applicable law.
                </p>
            </li>
            <li>
                <p>
                    <u>Restricted Use.</u> You may not copy, reproduce, modify, transmit, distribute, scrape, reverse engineer,
                    or create derivative works based on the Site, any account, or Content (as defined below) except as expressly
                    permitted by the Company in writing, and by accessing the Site you further agree:
                </p>
                <ul class="sub-decimal-list wide-decimal-list" style="--prefix: '2.3'">
                    <li>
                        <p>You will not use the Site for unlawful purposes or in violation of the Terms;</p>
                    </li>
                    <li>
                        <p>You will not try to gain unauthorized access to the Site or related systems;</p>
                    </li>
                    <li>
                        <p>You will not upload or transmit malware or harmful code;</p>
                    </li>
                    <li>
                        <p>You will not perform any automated data harvesting, scraping, or indexing;</p>
                    </li>
                    <li>
                        <p>You will not misrepresent your identity or affiliations; and</p>
                    </li>
                    <li>
                        <p>You will not use the Site to engage in services in violation of applicable law.</p>
                    </li>
                </ul>
            </li>
            <li>
                <p>
                    <u>Improper Use.</u> In the event of any known or suspected violation of these Terms or any improper use of
                    the Site or any related Company system, including use for an unlawful purpose, the Company reserves all
                    rights. You agree to promptly notify the Company in writing of any known or suspected improper use or
                    security issue.
                </p>
            </li>
            <li>
                <p>
                    <u>Right to Refuse; Discontinue Access.</u> The existence of this Site and creation of any account is not a
                    promise or guaranty to continued access to the Site or continued use of an account or receipt of services
                    from the Company. The Company may terminate, suspend, or refuse Site access to anyone at any time for any
                    reason in its sole discretion.
                </p>
            </li>
        </ul>
    </section>

    <!-- 3. Customer Accounts -->
    <section>
        <h3>3. Customer Accounts</h3>
        <ul class="decimal-list" style="--prefix: '3'">
            <li>
                <p>
                    <u>Accounts.</u> Whenever the Site is used by a customer or other person seeking to become a customer,
                    contractor, or other party and account creation is required, you agree to provide accurate information and
                    maintain the security of your account and confidentiality of your credentials.
                </p>
            </li>
            <li>
                <p>
                    <u>Account Creation.</u> We reserve the right to accept or reject any request for account creation and may
                    terminate an existing account or refuse and modify account access at any time for any reason in our sole
                    discretion.
                </p>
            </li>
            <li>
                <p>
                    <u>User Submissions.</u> By submitting or transmitting any information, documents, data, messages, files, or
                    materials through the Site or any related Company system (collectively, <u>“Submissions”</u>), you grant the
                    Company a perpetual, irrevocable, worldwide, royalty‑free, fully sublicensable license to use, host, store,
                    reproduce, modify, transmit, distribute, display, and create derivative works from such Submissions as
                    necessary or useful to:
                    (i) operate, manage, and maintain your account;
                    (ii) provide, deliver, or improve Company services;
                    (iii) perform administrative, commercial, operational, quality, training, analytics, compliance, or security functions;
                    (iv) support, develop, or enhance the Site and related Company systems;
                    (v) make disclosures to agencies and other parties.
                    This license extends to the Company’s Related Parties and to Third‑Party Providers to the extent required
                    to perform or support Company services. You represent and warrant that you have all rights, authority,
                    consents, and permissions necessary to submit the Submissions and to grant the license described in this
                    Section, and that your Submissions do not violate any laws, infringe third‑party rights, or contain any
                    harmful, unlawful, or misleading content. You remain solely responsible for the accuracy, completeness,
                    legality, and integrity of all Submissions, including all data, descriptions, account information,
                    documentation, and communications provided through the Site. The Company is not responsible for delays,
                    losses, penalties, or consequences arising out of inaccurate or incomplete Submissions.
                </p>
            </li>
            <li>
                <p>
                    <u>Account Activity.</u> You are responsible for your account and all activity conducted under your account
                    and agree to all applicable T&C in exchange for the account.
                </p>
            </li>
            <li>
                <p>
                    <u>Electronic Signatures & Communication.</u> By submitting forms, inquiries, or onboarding information
                    through the Site, you acknowledge and agree to consent to the use of electronic signatures and to receive
                    electronic communications through your account and via email, telephone, text, and other form of
                    communication to the fullest extent permitted by law.
                </p>
            </li>
        </ul>
    </section>

    <!-- 4. Data; Ownership; Trademarks -->
    <section>
        <h3>4. Data; Ownership; Trademarks</h3>
        <ul class="decimal-list" style="--prefix: '4'">
            <li>
                <p><u>Data Privacy.</u> By using the Site you acknowledge and consent to the data practices described in the Privacy Policy.</p>
            </li>
            <li>
                <p>
                    <u>Ownership.</u> The Site and all content, data, trademarks, graphics, images, software, and other materials
                    (<u>“Content”</u>) are owned by the Company or its licensors and are protected by all applicable T&C and to the
                    greatest extent of applicable intellectual property laws.
                </p>
            </li>
            <li>
                <p>
                    <u>Trademark.</u> “W8 Shipping” and its logo are a registered trademark. All rights are reserved.
                </p>
            </li>
        </ul>
    </section>

    <!-- 5. Compliance with Law; Non-Circumvention -->
    <section>
        <h3>5. Compliance with Law; Non-Circumvention</h3>
        <ul class="decimal-list" style="--prefix: '5'">
            <li>
                <p>
                    <u>Compliance.</u> You agree your use of the Site will comply with applicable laws, including laws relating to
                    trade secrets, data privacy, consumer protection, oceanic or surface transportation, customs and export
                    control, sanctioned parties or territories, anti-corruption, anti-money-laundering, or anti-boycott laws.
                </p>
            </li>
            <li>
                <p>
                    <u>Sanctions.</u> You may not access or use the Site from any sanctioned or embargoed territories and may not
                    engage with the Site to solicit or facilitate services or any transactions involving sanctioned or
                    restricted parties, destinations, or end-uses.
                </p>
            </li>
            <li>
                <p>
                    <u>Non-Circumvention.</u> You agree not to circumvent the Company by directly engaging vendors, carriers,
                    agents, or partners identified through the Site or the Company’s services for substantially similar
                    services without prior written consent.
                </p>
            </li>
            <li>
                <p>
                    <u>International Compliance.</u> You are responsible for ensuring your access to and use of the Site complies
                    with all applicable local laws, rules, regulations, controls, and other rules of the appropriate
                    jurisdiction from where you access the Site.
                </p>
            </li>
        </ul>
    </section>

    <!-- 6. Disclaimers -->
    <section>
        <h3>6. Disclaimers</h3>
        <ul class="decimal-list" style="--prefix: '6'">
            <li>
                <p>
                    <u>No Representations.</u> The Site and all its content and related systems are provided “AS IS” and “AS
                    AVAILABLE” and the Company hereby expressly disclaims all warranties, express or implied, including
                    merchantability, fitness for a particular purpose, title, and non-infringement.
                </p>
            </li>
            <li>
                <p>
                    <u>Third‑Party Providers.</u> The Site may display, reference, or provide access to services, information, or
                    content supplied by independent third‑party carriers, vendors, marketplaces, logistics providers, or other
                    service providers (<u>“Third‑Party Providers”</u>). The Company does not control, endorse, or assume
                    responsibility for any Third‑Party Provider or its services, content, pricing, availability,
                    representations, or practices. Your interactions or transactions with any Third‑Party Provider are solely
                    between you and that provider and may be governed by separate terms.
                </p>
            </li>
            <li>
                <p>
                    <u>Third-Party Provider Content.</u> The Site may contain content, systems, information, logos, or links to
                    certain Third-Party Provider content or systems which shall remain the exclusive property of the
                    Third-Party Provider. The Company shall not be responsible or liable for the content, security, or
                    practices of Third-Party Providers except as otherwise agreed in writing or the Service Terms.
                </p>
            </li>
            <li>
                <p><u>Compliance with Law.</u> This Site is intended to comply with the applicable laws.</p>
            </li>
            <li>
                <p>
                    <u>No Advice; No Guarantee.</u> Information made available through the Site, including pricing, rates,
                    schedules, transit times, data, or compliance‑related information, is provided for general reference only
                    and does not constitute legal, regulatory, customs, or professional advice. We do not guarantee accuracy,
                    completeness, or timeliness of such information, and all data regarding availability, pricing, and
                    schedules remain subject to applicable carriers and Third-Party Providers.
                </p>
            </li>
            <li>
                <p>
                    <u>No Agency or Joint Venture.</u> Your use of the Site does not create any agency, partnership, joint
                    venture, employment, or fiduciary relationship between you and the Company or between the Company and any
                    Third‑Party Provider. The Company does not act as your agent or the agent of any carrier or vendor unless
                    expressly stated in a separate written agreement or the Service Terms.
                </p>
            </li>
        </ul>
    </section>

    <!-- 7. Liability; Indemnification -->
    <section>
        <h3>7. Liability; Indemnification</h3>
        <ul class="decimal-list" style="--prefix: '7'">
            <li>
                <p>
                    <u>Limited Liability.</u> To the maximum extent permitted by law, neither the company nor any related
                    parties will be liable for any indirect, incidental, special, consequential, exemplary, or punitive
                    damages. By accessing the site you agree the total liability the company and any related parties may have
                    for any final non-appealable claim arising from or relating to the site may in no event in excess of One
                    Hundred Dollars ($100 USD), and by accessing the site you agree to affirmatively waive, release, and forgo
                    any and all claims to the contrary or liability exceeding the liability cap described here and hold the
                    Company and Related Parties harmless and will further indemnify it and them for any claims made contrary to
                    this Section.
                </p>
            </li>
            <li>
                <p>
                    <u>Indemnification.</u> You agree to indemnify, defend, and hold harmless the Company and all its Related
                    Parties from claims arising from your use of the Site or violation of these Terms or applicable law.
                </p>
            </li>
        </ul>
    </section>

    <!-- 8. Miscellaneous Terms -->
    <section>
        <h3>8. Miscellaneous Terms</h3>
        <ul class="decimal-list" style="--prefix: '8'">
            <li>
                <p>
                    <u>Injunctive Relief.</u> In the event of any known or reasonably suspected violation of the Terms, the
                    Company shall be entitled to take all commercially reasonable measures and shall be entitled to immediate
                    injunctive relief without the necessity of bond.
                </p>
            </li>
            <li>
                <p>
                    <u>Amendments.</u> These Terms may be amended by us at any time and your continued use and access to the Site
                    will constitute automatic acceptance of the amended or revised Terms.
                </p>
            </li>
            <li>
                <p>
                    <u>Arbitration.</u> Any dispute arising out of these Terms or the Site shall be initiated in binding
                    arbitration administered by a commercial arbitration association selected by the Company from time to
                    time. Notwithstanding the foregoing, the Company may seek injunctive or other equitable relief in any court
                    of competent jurisdiction for violations involving intellectual property rights, misuse of the Site, or
                    unauthorized access.
                </p>
            </li>
            <li>
                <p>
                    <u>Governing Law; Venue.</u> These Terms are governed by the laws of the State of Georgia, USA. Regardless
                    of the location from which you access the Site or the jurisdiction in which you reside, any claim or legal
                    proceeding arising out of or relating to these Terms or the Site must be filed exclusively in the state
                    courts located in Atlanta, Georgia.
                </p>
            </li>
            <li>
                <p>
                    <u>Class Action; Collective Action.</u> You acknowledge and agree that all claims must be brought in an
                    individual capacity, and by accessing the Site or obtaining services through the Site you unconditionally
                    and irrevocably waive the right to join or participate in any class action or collective action proceeding
                    and agree not to take part in any such claims.
                </p>
            </li>
            <li>
                <p>
                    <u>No Waiver.</u> No failure or delay by the Company in exercising any right, power, or remedy under these
                    Terms shall operate as a waiver of such right, power, or remedy, nor shall any single or partial exercise
                    of any such right, power, or remedy preclude any other or further exercise thereof. Any waiver must be in
                    a writing executed by an authorized representative of the Company to be effective.
                </p>
            </li>
            <li>
                <p><u>Notices.</u> To the extent notice is required it shall be sent electronically to: <a href="mailto:legal@w8shipping.com">legal@w8shipping.com</a></p>
            </li>
            <li>
                <p>
                    <u>Entire Agreement.</u> These Terms, together with any applicable policies, disclosures, or Service Terms
                    which are expressly incorporated by reference herein, shall constitute the entire agreement between you
                    and the Company regarding your access to and use of the Site and they supersede all prior or
                    contemporaneous agreements, understandings, negotiations, representations, and warranties, whether written
                    or oral, relating to the subject matter hereof.
                </p>
            </li>
            <li>
                <p>
                    <u>Severability.</u> If any provision of these Terms is determined to be invalid, illegal, or unenforceable
                    by a binding legal authority, such provision shall be deemed modified to the minimum extent necessary to
                    make it enforceable and consistent with its original intent. In the event it cannot be so modified, the
                    impacted provision shall be severed from these Terms and all remaining provisions shall remain in full
                    force and effect.
                </p>
            </li>
            <li>
                <p>
                    <u>Rights and Remedies.</u> The Company expressly reserves all rights or remedies available at law or in
                    equity on behalf of itself, its Related Parties, and any Third-Party Providers (to the extent applicable),
                    and nothing in these Terms shall be construed as limiting, modifying, or impairing such rights and
                    remedies.
                </p>
            </li>
            <li>
                <p>
                    <u>Termination.</u> The Company may suspend, restrict, or terminate your access to the Site or any related
                    account, in whole or in part, at any time and without prior notice, for any reason or no reason, including
                    any actual or suspected violation of these Terms, misuse of the Site, unlawful activity, failure to
                    maintain accurate information, or conduct that the Company determines may harm or not in the best
                    interests of the Company, its Related Parties, any Third-Party Providers, other users, or the security or
                    integrity of the Site. Upon termination, all rights granted to you under these Terms will immediately
                    cease, and the Company may remove, disable, delete, or otherwise dispose of any account records,
                    information, Submissions, or other data associated with you, your account, any services, or your access to
                    the Site, to the greatest extent of applicable law. Termination does not limit or release you from any
                    obligations or liabilities incurred prior to the effective date of termination, and any provisions of
                    these Terms that by their nature should survive termination—including those relating to ownership,
                    Submissions, indemnification, limitation of liability, governing law, venue, arbitration, and rights and
                    remedies—shall continue in full force and effect. The Company shall not be liable to you for any
                    suspension or termination of access to the Site or any account and has no duty to transfer, assign, or make
                    available any such records, data, or submissions.
                </p>
            </li>
            <li>
                <p>
                    <u>Accessibility.</u> We aim for substantial conformance with WCAG 2.1 AA and applicable ADA standards and
                    are continuously improving our website’s accessibility, however, if you need assistance or encounter a
                    barrier, please email <a href="mailto:legal@w8shipping.com">legal@w8shipping.com</a> or call us and a member of our team will provide the
                    information, item, or transaction through an accessible alternative and work to remediate the issue. Be
                    advised some Third-Party Provider features are outside our control, but we encourage all our vendors to
                    align with the highest level of accessibility standards.
                </p>
            </li>
        </ul>
    </section>`;
